import { useState } from 'react';
import { Button, Dialog, Label, Textarea } from '../components/ui';
import { useActiveWeatherHolds } from '../lib/queries';
import { useClearWeatherHold, useSetWeatherHold } from '../lib/mutations';
import { formatTimestamp } from '../lib/utils';

interface WeatherHoldDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    stageId: string | null;
    jobLabel: string;
}

export function WeatherHoldDialog({ open, onClose, jobId, stageId, jobLabel }: WeatherHoldDialogProps) {
    const { data: activeHolds = [] } = useActiveWeatherHolds(jobId);
    const setMut = useSetWeatherHold();
    const clearMut = useClearWeatherHold();
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Active hold that matches the current (job, stage) scope - the one
    // the "set" action would return from the idempotent RPC.
    const matchingHold = activeHolds.find((h) => h.stage_id === stageId);

    async function handleSet() {
        setError(null);
        if (!reason.trim()) {
            setError('Describe why you\'re setting the hold.');
            return;
        }
        try {
            await setMut.mutateAsync({
                p_job_id: jobId,
                p_stage_id: stageId,
                p_reason: reason.trim(),
            });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    async function handleClear(holdId: string) {
        setError(null);
        try {
            await clearMut.mutateAsync({ p_hold_id: holdId });
            if (!matchingHold || matchingHold.id === holdId) onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Clear failed');
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Weather hold"
            subtitle={jobLabel}
        >
            {activeHolds.length > 0 ? (
                <div className="mb-5">
                    <Label>Active holds</Label>
                    <div className="space-y-2">
                        {activeHolds.map((h) => (
                            <div
                                key={h.id}
                                className="border border-hold-500/30 bg-hold-500/5 rounded px-3 py-2.5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-graphite-900 dark:text-graphite-100 break-words">
                                            {h.reason}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-500 mt-1">
                                            Set by {h.set_by_email ?? 'unknown'} ·{' '}
                                            {formatTimestamp(h.hold_start)}
                                        </div>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleClear(h.id)}
                                        disabled={clearMut.isPending}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {!matchingHold ? (
                <div className="space-y-3">
                    <div>
                        <Label htmlFor="reason">Reason</Label>
                        <Textarea
                            id="reason"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ground saturated, 2 inches overnight. Need 48 hrs to dry."
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={setMut.isPending}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSet}
                            disabled={setMut.isPending || !reason.trim()}
                        >
                            {setMut.isPending ? 'Setting...' : 'Set hold'}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-end">
                    <Button variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                </div>
            )}

            {error ? (
                <div className="mt-4 border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                    {error}
                </div>
            ) : null}
        </Dialog>
    );
}
